export const ChatNameLiveChat = 'LiveChat';

const LIVECHAT_VISIBILITY_ATTR = 'data-livechat-visibility';

// Reflect the LiveChat widget's visibility onto a document-root attribute so the
// scoped mobile CSS can hide the persistent launcher whenever the widget is not
// maximized. Default to 'minimized' (non-visible launcher) for any missing value.
const applyVisibilityState = visibility => {
  document.documentElement.setAttribute(LIVECHAT_VISIBILITY_ATTR, visibility || 'minimized');
};

const insertScript = ({ async, id, rawScript, src }) => {
  const script = document.createElement('script');
  const firstScriptNode = document.getElementsByTagName('script')[0];

  if (async) {
    script.async = true;
  }

  if (src) {
    script.src = src;
  }

  if (id) {
    script.setAttribute('id', id);
  }

  if (rawScript) {
    script.append(rawScript);
  }

  firstScriptNode.parentNode.insertBefore(script, firstScriptNode);

  return script;
};

const liveChatIntegration = config => {
  const { accountEmail, accountId, accountName, chatParams } = config;
  const { param1: widgetId } = chatParams;

  window.__lc = window.__lc || {};
  window.__lc.license = widgetId;

  const api = {
    _h: null,
    _q: [],
    _v: '2.0',
  };

  const callApi = call => (api._h ? api._h.apply(null, call) : api._q.push(call));

  api.on = (...params) => {
    callApi(['on', params]);
  };
  api.once = (...params) => {
    callApi(['once', params]);
  };
  api.off = (...params) => {
    callApi(['off', params]);
  };
  api.get = (...params) => {
    if (!api._h) {
      throw new Error("[LiveChatWidget] You can't use getters before load.");
    }

    return callApi(['get', params]);
  };
  api.call = (...params) => {
    callApi(['call', params]);
  };
  api.init = () => {
    const script = document.createElement('script');
    script.async = true;
    script.type = 'text/javascript';
    script.src = 'https://cdn.livechatinc.com/tracking.js';
    document.head.appendChild(script);
  };

  !window.__lc.asyncInit && api.init();
  window.LiveChatWidget = window.LiveChatWidget || api;

  api.call('update_session_variables', {
    accountEmail,
    accountId,
    accountName,
  });

  // Reveal the widget when it is maximized OR while LiveChat is showing a
  // greeting (greetings are displayed in the 'minimized' state); otherwise keep
  // the idle launcher hidden. Reuses applyVisibilityState('maximized') to trigger
  // the CSS reveal.
  let widgetVisibility = 'minimized';
  let isGreetingActive = false;

  const syncLiveChatVisibility = () => {
    const shouldReveal = widgetVisibility === 'maximized' || isGreetingActive;
    applyVisibilityState(shouldReveal ? 'maximized' : widgetVisibility);
  };

  // Default to a non-visible launcher before any event fires (prevents flash).
  syncLiveChatVisibility();

  api.on('ready', data => {
    widgetVisibility = data?.state?.visibility || 'minimized';
    syncLiveChatVisibility();
  });
  api.on('visibility_changed', data => {
    widgetVisibility = data?.visibility || 'minimized';
    syncLiveChatVisibility();
  });
  api.on('greeting_displayed', () => {
    isGreetingActive = true;
    syncLiveChatVisibility();
  });
  api.on('greeting_hidden', () => {
    isGreetingActive = false;
    syncLiveChatVisibility();
  });
};

const tawkToChatIntegration = config => {
  const { accountEmail, accountName, chatParams } = config;
  const { param1: widgetId, param2: accountKey } = chatParams;

  insertScript({
    async: true,
    src: `https://embed.tawk.to/${accountKey}/${widgetId}`,
  });

  window.Tawk_API = window.Tawk_API || {};
  window.Tawk_LoadStart = new Date();

  window.Tawk_API.onLoad = function () {
    window.Tawk_API.setAttributes({
      visitor: {
        email: accountEmail,
        name: accountName,
      },
    });
    // TODO: Timeout used for minimize window if page was reload and window was maximize
    setTimeout(() => {
      window.Tawk_API.minimize();
      window.Tawk_API.hideWidget();
    }, 0);

    if (window.Tawk_API.onLoaded) {
      setTimeout(() => {
        window.Tawk_Window.minWidget.$el.setAttribute('data-id', 'tawk-to-min-widget');
        window.Tawk_Window.maxWidget.$el.setAttribute('data-id', 'tawk-to-max-widget');
      }, 500);
    }
  };
};

const respondIOChatIntegration = config => {
  const { chatParams } = config;
  const { param1: key } = chatParams;
  if (!key) return;

  insertScript({
    async: true,
    id: 'respondio__widget',
    src: `https://cdn.respond.io/webchat/widget/widget.js?cId=${key}`,
  });

  window.$respond = window.$respond || {};
};

const octadeskChatIntegrtation = config => {
  const { chatParams } = config;
  const { param1: subDomain } = chatParams;
  if (!subDomain) return null;

  insertScript({
    async: true,
    rawScript: `
    (function (o, c, t, a, d, e, s, k) {
     o.octadesk = o.octadesk || {};
     o.octadesk.chatOptions = {
     subDomain: a,
     showButton: d,
     openOnMessage: e,
     hide: s
     };
     var bd = c.getElementsByTagName("body")[0];
     var sc = c.createElement("script");
     sc.async = 1;
     sc.src = t;
     bd.appendChild(sc);
     })(window, document, 'https://cdn.octadesk.com/embed.js', '${subDomain}', 'false', 'true', 'false');
  `,
  });
};

const zendeskChatIntegration = config => {
  const { chatParams } = config;
  const { param1: widgetId, param2: locale, param3: offset } = chatParams;
  if (!widgetId) return;

  const script = insertScript({
    async: true,
    id: 'ze-snippet',
    src: `https://static.zdassets.com/ekr/snippet.js?key=${widgetId}`,
  });

  window.zE = window.zE || {};

  script.onload = () => {
    if (typeof window.zE === 'function') {
      window.zE('messenger', 'hide');
      window.zE('messenger:on', 'close', () => {
        window.zE('messenger', 'hide');
      });

      if (locale) {
        window.zE('messenger:set', 'locale', locale);
      }

      if (offset) {
        window.zESettings = {
          webWidget: {
            offset: {
              mobile: {
                vertical: `${offset}px`,
              },
            },
          },
        };
      }
    }
  };
};

const infobipChatIntegration = config => {
  const { chatParams } = config;
  const { param1: apiKey } = chatParams;
  if (!apiKey) return;

  insertScript({
    async: true,
    id: 'infobip-widget',
    rawScript: `
      (function(I,n,f,o,b,i,p){
        I[b]=I[b]||function(){(I[b].q=I[b].q||[]).push(arguments)};
        I[b].t=1*new Date();i=n.createElement(f);i.async=1;i.src=o;
        p=n.getElementsByTagName(f)[0];p.parentNode.insertBefore(i,p)
      })(window,document,'script','https://livechat.infobip.com/widget.js','liveChat');
    `,
  });
  window.liveChat('init', apiKey);
};

export const createChat = (name, config = {}) => {
  const chatMap = {
    Infobip: infobipChatIntegration,
    LiveChat: liveChatIntegration,
    Octadesk: octadeskChatIntegrtation,
    'Respond.io': respondIOChatIntegration,
    'Tawk.to': tawkToChatIntegration,
    Zendesk: zendeskChatIntegration,
    ZendeskWebWidget: zendeskChatIntegration,
  };
  const chatInstance = chatMap[name];
  // eslint-disable-next-line no-unused-expressions
  chatInstance && chatInstance(config);
};

export const updateChat = (name, config) => {
  const { accountEmail = '', accountId = null, accountName = '', chatParams = {} } = config;

  if (name === ChatNameLiveChat) {
    window.LiveChatWidget.call('update_session_variables', {
      accountEmail,
      accountId,
      accountName,
      chatParams,
    });
  }
};
